import { FC } from 'react';
import { ScheduledCall } from '@teachur/http-client';
import { Calendar, Clock, PhoneCall, Hourglass, XCircle, PhoneMissed } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { formatTime, formatFullDate } from '../../lib/helpers';
import { CancelScheduledCallAction } from '@new/features/courses/components/CourseCompletionSteps/StepContent/HearingContent/CancelScheduledCallAction';

interface AgendaListProps {
  scheduledCalls: ScheduledCall[];
  onJoinCall: (videoCallId: string) => void;
}

type CallState = 'in_progress' | 'joinable' | 'rejected' | 'pending';

const getCallState = (call: ScheduledCall): CallState => {
  if (call.status === 'rejected') return 'rejected';
  if (call.videoCall?.startedAt) return 'in_progress';
  if (call.videoCallId) return 'joinable';
  return 'pending';
};

export const AgendaList: FC<AgendaListProps> = ({ scheduledCalls, onJoinCall }) => {
  if (!scheduledCalls.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <Clock className="h-8 w-8 text-(--neutral-black-NB500)!" />
        <p className="text-sm text-(--neutral-black-NB500)! m-0!">
          No scheduled calls for this day
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-100 overflow-y-auto custom-scrollbar">
      {scheduledCalls.map((call) => {
        const callTitle =
          call.type === 'onboarding' ? 'Onboarding Call' : call.course?.name || 'Course Hearing';
        const callState = getCallState(call);
        const canManageCall = callState === 'in_progress' || callState === 'joinable';

        const accentColor =
          callState === 'in_progress'
            ? 'bg-(--warning-W700)!'
            : callState === 'rejected'
              ? 'bg-(--danger-D700)!'
              : callState === 'pending'
                ? 'bg-(--neutral-black-NB300)!'
                : 'bg-(--secondary-B500)]!';

        return (
          <div
            key={call.id}
            className="mb-3 flex h-21.25 justify-between gap-4 rounded-xl border border-(--neutral-black-NB100)! bg-(--neutral-white-NW900)! p-4 last:mb-0"
          >
            <div className={`w-1 rounded-xl ${accentColor}`} />

            <div className="flex w-full items-center justify-between gap-4">
              <div>
                <h5 className="mb-2! text-sm font-bold text-(--neutral-black-NB900)!">
                  {callTitle}
                </h5>

                <div className="flex items-center gap-4 text-xs text-(--neutral-black-NB600)!">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{formatFullDate(call.startTime)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{formatTime(call.startTime)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {canManageCall && (
                  <Button
                    onClick={() => call.videoCallId && onJoinCall(call.videoCallId)}
                    variant="secondary"
                    className="hover:bg-(--success-S200)! rounded-full!"
                  >
                    <PhoneCall size={12} className="text-(--success-S700)" />
                  </Button>
                )}

                {canManageCall && (
                  <CancelScheduledCallAction call={call}>
                    <Button variant="secondary" className="hover:bg-(--danger-D200)! rounded-full!">
                      <PhoneMissed size={12} className="text-(--danger-D700)" />
                    </Button>
                  </CancelScheduledCallAction>
                )}
                {callState === 'pending' && (
                  <div className="flex items-center gap-1 text-xs font-medium text-(--neutral-black-NB400)!">
                    <Hourglass size={14} />
                    <span>Pending</span>
                  </div>
                )}

                {callState === 'rejected' && (
                  <div className="flex items-center gap-1 text-xs font-medium text-(--danger-D700)!">
                    <XCircle size={14} />
                    <span>Rejected</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
